import { useCallback, useEffect, useState } from 'react';
import { FirebaseMessaging, Importance } from '@capacitor-firebase/messaging';
// Aliased: the plugin's `Notification` would shadow the DOM global of the same name.
import type {
  Notification as PushNotification,
  NotificationActionPerformedEvent,
} from '@capacitor-firebase/messaging';
import { Capacitor } from '@capacitor/core';
import type { PermissionState } from '@capacitor/core';
import { captureError } from '../services/posthog';
import {
  isPushOptedOut,
  PUSH_TOKEN_STORAGE_KEY,
  PUSH_TOKEN_UPDATED_STORAGE_KEY,
  setPushOptedOut,
} from '../services/pushTokens';

/**
 * The Android notification channel backgrounded notifications land on. Must stay in sync with
 * `default_notification_channel_id` in `android/app/src/main/res/values/strings.xml`, which the
 * manifest points FCM at: FCM names the channel, but Android only displays on a channel the app
 * has actually created, so both halves have to agree.
 */
const ANDROID_DEFAULT_CHANNEL_ID = 'agencyos_default';

export interface PushNotificationState {
  /**
   * The FCM registration token — the address the backend sends to, on both platforms. Note this
   * is not the raw APNs token on iOS: Firebase exchanges the APNs token for an FCM one, so a
   * single send path serves both platforms.
   */
  token: string | null;
  isRegistered: boolean;
  /** Capacitor's permission states, plus 'unknown' before the first check resolves. */
  permissionStatus: PermissionState | 'unknown';
  lastNotification: PushNotification | null;
}

export interface UsePushNotificationsReturn extends PushNotificationState {
  requestPermission: () => Promise<boolean>;
  register: () => Promise<void>;
  /** Delete this device's FCM token, so FCM stops delivering to it until register() runs again. */
  unregister: () => Promise<void>;
}

/**
 * Hook to manage push notifications for iOS and Android, over Firebase Cloud Messaging.
 *
 * Features:
 * - Request permission for push notifications
 * - Acquire and refresh the FCM registration token
 * - Process incoming notifications (foreground, background, tap)
 * - Store notification state
 *
 * @param onNotificationReceived - Callback when notification is received
 * @param onNotificationTapped - Callback when notification is tapped
 * @param autoRegister - Acquire a token on mount if permission is already granted and the user has not
 *   turned push off on this device (default: false)
 */
export function usePushNotifications(
  onNotificationReceived?: (notification: PushNotification) => void,
  onNotificationTapped?: (action: NotificationActionPerformedEvent) => void,
  autoRegister: boolean = false
): UsePushNotificationsReturn {
  const [state, setState] = useState<PushNotificationState>({
    token: null,
    isRegistered: false,
    permissionStatus: 'unknown',
    lastNotification: null,
  });

  const isNative = Capacitor.isNativePlatform();

  // Unlike the old APNs-direct plugin, there is no register()-then-wait-for-a-listener dance:
  // getToken() resolves with the FCM token directly. The tokenReceived listener below only
  // covers later rotations.
  const register = useCallback(async (): Promise<void> => {
    if (!isNative) {
      console.warn('[usePushNotifications] Cannot register on web');
      return;
    }

    try {
      const { token } = await FirebaseMessaging.getToken();
      console.log('[PushNotifications] FCM token acquired');
      setState(prev => ({ ...prev, token, isRegistered: true }));
      localStorage.setItem(PUSH_TOKEN_STORAGE_KEY, token);
      localStorage.setItem(PUSH_TOKEN_UPDATED_STORAGE_KEY, new Date().toISOString());
      setPushOptedOut(false);
    } catch (error) {
      console.error('[usePushNotifications] Token acquisition error:', error);
      captureError(error, {
        component: 'usePushNotifications',
        event: 'getTokenError',
      });
      setState(prev => ({ ...prev, isRegistered: false }));
      throw error;
    }
  }, [isNative]);

  // The permission stays granted: turning push off here is per device and reversible from the same
  // screen, while revoking permission is only possible from the OS settings.
  const unregister = useCallback(async (): Promise<void> => {
    if (!isNative) return;

    try {
      await FirebaseMessaging.deleteToken();
    } catch (error) {
      console.error('[usePushNotifications] Token deletion error:', error);
      captureError(error, {
        component: 'usePushNotifications',
        event: 'deleteTokenError',
      });
      throw error;
    } finally {
      // Recorded even when deleteToken fails: the backend has already let go of the token by now, and
      // the opt-out is what stops the next launch from registering the device again.
      setPushOptedOut(true);
      localStorage.removeItem(PUSH_TOKEN_STORAGE_KEY);
      localStorage.removeItem(PUSH_TOKEN_UPDATED_STORAGE_KEY);
      setState(prev => ({ ...prev, token: null, isRegistered: false }));
    }
  }, [isNative]);

  useEffect(() => {
    if (!isNative) return;

    let isMounted = true;

    const initializePushNotifications = async () => {
      try {
        // Create the channel before anything can be delivered to it. Repeat calls with the same
        // id are a no-op, so this is safe on every mount. Android-only API.
        if (Capacitor.getPlatform() === 'android') {
          await FirebaseMessaging.createChannel({
            id: ANDROID_DEFAULT_CHANNEL_ID,
            name: 'General',
            description: 'Updates about your insurance operations',
            importance: Importance.High,
          });
        }

        const permStatus = await FirebaseMessaging.checkPermissions();

        if (isMounted) {
          setState(prev => ({
            ...prev,
            permissionStatus: permStatus.receive,
          }));
        }

        if (autoRegister && permStatus.receive === 'granted' && !isPushOptedOut()) {
          await register();
        }

        // Fires when FCM rotates the token, not on the initial acquisition — that comes back
        // from getToken() in register().
        await FirebaseMessaging.addListener('tokenReceived', ({ token }) => {
          console.log('[PushNotifications] FCM token refreshed');

          if (isMounted) {
            setState(prev => ({ ...prev, token, isRegistered: true }));
          }

          localStorage.setItem(PUSH_TOKEN_STORAGE_KEY, token);
          localStorage.setItem(PUSH_TOKEN_UPDATED_STORAGE_KEY, new Date().toISOString());
        });

        // Notification arriving while the app is in the foreground.
        await FirebaseMessaging.addListener('notificationReceived', ({ notification }) => {
          console.log('[PushNotifications] Notification received:', notification);

          if (isMounted) {
            setState(prev => ({ ...prev, lastNotification: notification }));
          }

          onNotificationReceived?.(notification);
        });

        await FirebaseMessaging.addListener('notificationActionPerformed', (action) => {
          console.log('[PushNotifications] Notification action:', action);

          if (isMounted) {
            setState(prev => ({ ...prev, lastNotification: action.notification }));
          }

          onNotificationTapped?.(action);
        });
      } catch (error) {
        console.error('[usePushNotifications] Initialization error:', error);
        captureError(error, {
          component: 'usePushNotifications',
          event: 'initError',
        });
      }
    };

    initializePushNotifications();

    return () => {
      isMounted = false;
      FirebaseMessaging.removeAllListeners();
    };
  }, [isNative, autoRegister, onNotificationReceived, onNotificationTapped, register]);

  const requestPermission = async (): Promise<boolean> => {
    if (!isNative) {
      console.warn('[usePushNotifications] Cannot request permission on web');
      return false;
    }

    try {
      const permResult = await FirebaseMessaging.requestPermissions();
      const granted = permResult.receive === 'granted';

      setState(prev => ({
        ...prev,
        permissionStatus: permResult.receive,
      }));

      console.log('[PushNotifications] Permission result:', permResult);
      return granted;
    } catch (error) {
      console.error('[usePushNotifications] Permission request error:', error);
      captureError(error, {
        component: 'usePushNotifications',
        event: 'permissionError',
      });
      return false;
    }
  };

  return {
    ...state,
    requestPermission,
    register,
    unregister,
  };
}
